import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Sector, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { fetchTimeEntriesStats } from '../../../services/api';
import { stringToColor, ensureContrast } from '../../../services/utils';

const boxSx = {
    height: '100%',
    width: '100%',
    '& .recharts-wrapper svg': {
        overflow: 'inherit !important',
        '& .text1': {
            fill: (theme) => theme.palette.text.secondary
        },
        '& .text2': {
            fill: (theme) => theme.palette.text.disabled,
            fontSize: '13px'
        }
    }
};

const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value, isActive } = props;

    if (isActive) {
        return (
            <svg>
                <g>
                    <text x={cx} y={cy} dy={-10} textAnchor="middle" fill={fill}>
                        {payload.name}
                    </text>
                    <text x={cx} y={cy} dy={10} textAnchor="middle" className="text1">
                        {value}h
                    </text>
                    <text x={cx} y={cy} dy={30} textAnchor="middle" className="text2">
                        {`(${(percent * 100).toFixed(2)}%)`}
                    </text>
                    <Sector
                        cx={cx}
                        cy={cy}
                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        startAngle={startAngle}
                        endAngle={endAngle}
                        fill={fill}
                    />
                    <Sector
                        cx={cx}
                        cy={cy}
                        startAngle={startAngle}
                        endAngle={endAngle}
                        innerRadius={outerRadius + 6}
                        outerRadius={outerRadius + 10}
                        fill={fill}
                    />
                </g>
            </svg>
        );
    }

    return (
        <svg>
            <g>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
            </g>
        </svg>
    );
};

const TimeEntryPieChart = ({ projectId, typeId, userId, startDate, endDate, freq, groupBy, disableFetch = false, ...props }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [data, setData] = useState(props.data);
    const theme = useTheme();

    const formatData = (customData) => {
        if (customData) {
            const groupData = customData.reduce((acc, item) => {
                const foundIndex = acc.findIndex(r => r.name === item.name);

                if (foundIndex === -1) {
                    if (item.name) {
                        return [...acc, item];
                    }
                    return acc;
                }

                const updatedItem = { ...acc[foundIndex], value: acc[foundIndex].value + item.value };
                acc.splice(foundIndex, 1, updatedItem);
                return acc;
            }, []);

            setData(groupData);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (groupBy !== 'date') {
                const response = await fetchTimeEntriesStats({ project_id: projectId, type_id: typeId, user_id: userId, start_date: startDate, end_date: endDate, freq, group_by: groupBy });
                if (response?.data) {
                    formatData(response.data);
                }
            }
        };

        if (!disableFetch) {
            fetchData();
        }
    }, [projectId, typeId, userId, startDate, endDate, freq, groupBy, disableFetch]);

    useEffect(() => {
        if (props.data) {
            formatData(props.data);
        }
    }, [props.data]);

    const handleMouseEnter = (_, index) => {
        setActiveIndex(index);
    }

    if (!data || !groupBy || groupBy === 'date') {
        return null;
    }

    return (
        <Box sx={boxSx}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 200, height: 200 }}>
                <PieChart width={400} height={400}>
                    <Pie
                        activeIndex={activeIndex}
                        shape={renderActiveShape}
                        data={data}
                        cx="50%"
                        cy="50%"
                        innerRadius={90}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        onMouseEnter={handleMouseEnter}
                        style={{ overflow: 'unset' }}
                    >
                        {data.map((record, index) => {
                            let color;
                            if (record?.color) {
                                color = theme.palette.mode === 'dark' ? ensureContrast(record.color) : record.color;
                            }
                            else {
                                color = stringToColor(record.name, { dark: theme.palette.mode === 'dark' })
                            }
                            return (
                                <Cell key={`cell-${index}`} fill={color} />
                            );
                        })}
                    </Pie>
                    <Tooltip defaultIndex={activeIndex} content={renderActiveShape} active />
                </PieChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default TimeEntryPieChart;